import React from 'react';
import { Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Cancel from '@material-ui/icons/Cancel';

const useStyles = makeStyles({
  /**
   * A chip naming a file or a person has to be read in full -- half a filename identifies nothing,
   * and there is no way to widen the pill on a phone. So it grows to as many lines as its text
   * needs rather than keeping MUI's one fixed-height line and clipping the rest.
   */
  chip: {
    height: 'auto',
    maxWidth: '100%',
    minHeight: 24,
    padding: '3px 0px',
  },
  label: {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    // A long path or address carries no spaces to break at, so it is allowed to break anywhere
    // rather than push the pill past its container.
    overflowWrap: 'anywhere',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    paddingTop: 2,
    paddingBottom: 2,
  },
  /**
   * The remove control, in the icon slot so it leads the pill. MUI's own delete icon only ever sits
   * after the label, and on a wrapped pill that puts it wherever the last line happens to end.
   */
  remove: {
    cursor: 'pointer',
    flexShrink: 0,
    color: 'rgba(0, 0, 0, 0.26)',
    '&:hover': {
      color: 'rgba(0, 0, 0, 0.4)',
    },
  },
});

type Props = {
  label: string;
  /** Shown ahead of the label, after the remove control -- what kind of thing this names. */
  typeIcon?: React.ReactNode;
  onRemove: () => void;
};

/** A chip that shows its whole label, however long, and is cleared from its left-hand x. */
export default function RemovableChip(props: Props) {
  const classes = useStyles();
  const { label, typeIcon, onRemove } = props;

  return (
    <Chip
      size="small"
      classes={{ root: classes.chip, label: classes.label }}
      icon={
        <Cancel
          className={classes.remove}
          fontSize="small"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        />
      }
      label={
        <>
          {typeIcon}
          {label}
        </>
      }
    />
  );
}
